import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { buildSiteSettingsState } from '../lib/siteDefaults';
import { setBrand } from '../lib/brand';
import { hasPageTitle } from '../lib/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve a settings URL to something the browser can actually fetch.
 *
 * Uploaded assets come back as relative API paths (/api/storage/public/...).
 * Resolving those against window.location.origin breaks in development: the
 * frontend and the API are two different servers, so the browser asks the CRA
 * dev server for the image, gets index.html back with a 200, and renders a
 * broken image rather than a useful 404.
 *
 * API paths therefore resolve against the API origin. Everything else — files
 * served from /public, and absolute URLs — resolves against the page as before.
 *
 * In production REACT_APP_BACKEND_URL is empty and both share one origin, so
 * this collapses back to the original behaviour.
 */
const toAbsoluteUrl = (path) => {
  if (!path) return path;
  try {
    const base = path.startsWith('/api/') && API_URL ? API_URL : window.location.origin;
    return new URL(path, base).href;
  } catch (e) {
    return path;
  }
};

const SiteSettingsContext = createContext(buildSiteSettingsState({}, true));

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => buildSiteSettingsState({}, true));

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings/site`);
        const fetchedSettings = response.data;
        
        // Build full URLs for relative paths
        if (fetchedSettings.logo_url) {
          fetchedSettings.logo_url = toAbsoluteUrl(fetchedSettings.logo_url);
        }
        if (fetchedSettings.pwa_icon_url) {
          fetchedSettings.pwa_icon_url = toAbsoluteUrl(fetchedSettings.pwa_icon_url);
        }

        const nextSettings = buildSiteSettingsState(fetchedSettings, false);

        // Hand the brand to code that cannot read React context — seo.js,
        // useSeo.js and other plain modules. Without this they fall back to
        // their own constants, which is how hardcoded brand names survive a
        // rebrand. Must happen before anything renders meta tags.
        setBrand(nextSettings);

        // Only a fallback. Pages set their own titles through setSeoMetadata,
        // which re-applies them when the brand arrives; overwriting here is
        // what previously reduced every page title to just the business name.
        if (nextSettings.siteName && !hasPageTitle()) {
          document.title = nextSettings.siteName;
        }

        if (nextSettings.faviconUrl) {
          const faviconFullUrl = toAbsoluteUrl(nextSettings.faviconUrl);

          // Update ALL favicon-related link elements
          const faviconSelectors = [
            "link[rel='icon']",
            "link[rel='shortcut icon']",
            "link[rel='apple-touch-icon']"
          ];
          
          faviconSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              el.href = faviconFullUrl;
            });
          });
          
          // If no icon exists, create one
          if (!document.querySelector("link[rel='icon']")) {
            const iconLink = document.createElement('link');
            iconLink.type = 'image/x-icon';
            iconLink.rel = 'icon';
            iconLink.href = faviconFullUrl;
            document.head.appendChild(iconLink);
          }
        }

        window.__DEBUG_MODE__ = nextSettings.debugMode;
        if (nextSettings.debugMode) {
          console.log('[DEBUG MODE] Site settings loaded:', response.data);
        }

        setSettings(nextSettings);
      } catch (error) {
        console.error('Error fetching site settings:', error);
        window.__DEBUG_MODE__ = false;
        setSettings(buildSiteSettingsState({}, false));
      }
    };

    fetchSiteSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
};